import React, {
    useEffect,
    useRef,
} from 'react'
import {
    Check,
    X,
} from 'lucide-react'

export interface SuccessModalProps {
    isOpen: boolean;
    onClose: () => void;
    roomName: string;
    duration: number;
    startTime: Date;
    endTime: Date;
}

export function SuccessModal({ isOpen, onClose, roomName, duration, startTime, endTime }: SuccessModalProps) {
    const modalRef = useRef<HTMLDivElement>(null)

    // Handle clicks outside the modal and escape key
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
                onClose()
            }
        }

        function handleEscapeKey(event: KeyboardEvent) {
            if (event.key === 'Escape') {
                onClose()
            }
        }

        // Add event listeners when modal is shown
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside)
            document.addEventListener('keydown', handleEscapeKey)
        }

        // Clean up
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
            document.removeEventListener('keydown', handleEscapeKey)
        }
    }, [isOpen, onClose])

    if (!isOpen) {
        return null
    }

    return (
        <div className="fixed inset-0 bg-black/[.5] flex items-center justify-center z-50 p-4">
            <div
                ref={modalRef}
                className="bg-white rounded-[20px] p-6 max-w-md w-full shadow-lg"
            >
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-bold">Booking successful</h3>
                    <button
                        onClick={onClose}
                        className="p-1 rounded-full hover:bg-gray-100"
                    >
                        <X size={20} />
                    </button>
                </div>

                <div className="flex items-center gap-2 mb-4">
                    <Check className="w-5 h-5" />
                    <span>Room booked successfully!</span>
                </div>

                <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                    <div className="flex flex-col gap-2">
                        <div className="flex justify-between">
                            <span className="font-medium">Room:</span>
                            <span>{roomName}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="font-medium">Duration:</span>
                            <span>{duration} minutes</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="font-medium">Time:</span>
                            <span>
                                {startTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} -
                                {endTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                        </div>
                    </div>
                </div>

                <button
                    className="w-full h-10 px-4 py-2 cursor-pointer rounded-full flex items-center justify-center bg-black text-white"
                    onClick={onClose}
                >
                    <span>Close</span>
                </button>
            </div>
        </div>
    )
}
